import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import API from '../utils/api';
import { trackAffiliatePageViewed, trackAffiliateApplyStarted, trackAffiliateApplySubmitted } from '../utils/analytics';

// Countries where Stripe Connect payouts are supported
// Must match backend/utils/affiliateCountries.js
const SUPPORTED_COUNTRIES = [
  { code: 'AU', name: 'Australia' },
  { code: 'AT', name: 'Austria' },
  { code: 'BE', name: 'Belgium' },
  { code: 'CA', name: 'Canada' },
  { code: 'DK', name: 'Denmark' },
  { code: 'FI', name: 'Finland' },
  { code: 'FR', name: 'France' },
  { code: 'DE', name: 'Germany' },
  { code: 'HK', name: 'Hong Kong' },
  { code: 'IE', name: 'Ireland' },
  { code: 'IT', name: 'Italy' },
  { code: 'JP', name: 'Japan' },
  { code: 'LU', name: 'Luxembourg' },
  { code: 'MY', name: 'Malaysia' },
  { code: 'MX', name: 'Mexico' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'NZ', name: 'New Zealand' },
  { code: 'NO', name: 'Norway' },
  { code: 'PL', name: 'Poland' },
  { code: 'PT', name: 'Portugal' },
  { code: 'SG', name: 'Singapore' },
  { code: 'ES', name: 'Spain' },
  { code: 'SE', name: 'Sweden' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'AE', name: 'United Arab Emirates' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'US', name: 'United States' },
];

const SUPPORTED_CODES = new Set(SUPPORTED_COUNTRIES.map(c => c.code));

const AffiliateApplyPage = () => {
  const { user } = useAuth();
  const navigate  = useNavigate();

  const [existing, setExisting]     = useState(null);
  const [loading, setLoading]       = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]           = useState('');
  const [countryNotSupported, setCountryNotSupported] = useState(false);
  const [form, setForm] = useState({
    country:     '',
    websiteUrl:  '',
    bio:         '',
    promoMethod: '',
  });

  useEffect(() => {
    trackAffiliatePageViewed();
    API.get('/affiliate/me')
      .then(r => setExisting(r.data.affiliate))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleCountryChange = (e) => {
    const val = e.target.value;
    if (!form.country) trackAffiliateApplyStarted(); // first interaction = apply started
    setForm(f => ({ ...f, country: val }));
    setCountryNotSupported(val !== '' && !SUPPORTED_CODES.has(val));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.country) { setError('Please select your country.'); return; }
    if (countryNotSupported) return;
    setError('');
    setSubmitting(true);
    try {
      await API.post('/affiliate/apply', form);
      trackAffiliateApplySubmitted(form.country);
      const r = await API.get('/affiliate/me');
      setExisting(r.data.affiliate);
    } catch (err) {
      if (err.response?.data?.code === 'COUNTRY_NOT_SUPPORTED') {
        setCountryNotSupported(true);
      } else {
        setError(err.response?.data?.message || 'Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="d-flex justify-content-center align-items-center vh-100">
      <div className="spinner-border" style={{ color: '#6f42c1' }}></div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: 'var(--page-bg)' }}>
      {/* Header */}
      <div style={{ background: 'linear-gradient(135deg,#2d1b69,#6f42c1)', padding: '48px 24px', textAlign: 'center' }}>
        <div style={{ fontSize: '48px', marginBottom: '12px' }}>🤝</div>
        <h1 style={{ color: '#fff', fontFamily: "'Fredoka One',cursive", fontSize: '32px', margin: 0 }}>
          Dreamy Bedtime Affiliate Program
        </h1>
        <p style={{ color: '#c9a8ff', fontSize: '16px', marginTop: '10px', maxWidth: '520px', margin: '12px auto 0' }}>
          Share Dreamy Bedtime with your audience and earn commission on every paying customer you bring.
        </p>
      </div>

      <div style={{ maxWidth: '600px', margin: '0 auto', padding: '40px 24px' }}>

        {/* How it works */}
        <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '16px' }}>
          <div className="card-body p-4">
            <h5 className="fw-bold mb-3" style={{ color: 'var(--text-main)' }}>How it works</h5>
            {[
              { icon: '🔗', title: 'Get your unique link + promo code', desc: 'Once approved, we assign you a unique code. Share your link or tell your audience to use your code at checkout.' },
              { icon: '💰', title: 'Earn commission on every conversion', desc: 'When someone upgrades to a paid plan using your link or code, you earn commission for up to 12 months per customer.' },
              { icon: '🏦', title: 'Get paid directly to your bank', desc: 'Connect your bank via Stripe. Payouts are sent when your balance hits the threshold.' },
            ].map((s, i) => (
              <div key={i} className="d-flex gap-3 mb-3">
                <div style={{ fontSize: '24px', flexShrink: 0 }}>{s.icon}</div>
                <div>
                  <div className="fw-bold" style={{ color: 'var(--text-main)', fontSize: '14px' }}>{s.title}</div>
                  <div style={{ color: 'var(--text-muted2)', fontSize: '13px' }}>{s.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {existing ? (
          <div className="card border-0 shadow-sm" style={{ borderRadius: '16px' }}>
            <div className="card-body p-4 text-center">
              {existing.status === 'pending' && (
                <>
                  <div style={{ fontSize: '48px', marginBottom: '12px' }}>⏳</div>
                  <h5 className="fw-bold" style={{ color: 'var(--text-main)' }}>Application under review</h5>
                  <p style={{ color: 'var(--text-muted2)', fontSize: '14px' }}>
                    We'll review your application and assign you a unique code within 24–48 hours.
                  </p>
                </>
              )}
              {existing.status === 'active' && (
                <>
                  <div style={{ fontSize: '48px', marginBottom: '12px' }}>✅</div>
                  <h5 className="fw-bold" style={{ color: 'var(--text-main)' }}>You're an active affiliate!</h5>
                  <p style={{ color: 'var(--text-muted2)', fontSize: '14px' }}>Head to your dashboard to see your stats and get your link.</p>
                  <button className="btn rounded-pill px-4 fw-bold" style={{ background: '#6f42c1', color: 'white' }}
                    onClick={() => navigate('/affiliate/dashboard')}>
                    Go to Dashboard
                  </button>
                </>
              )}
              {existing.status === 'rejected' && (
                <>
                  <div style={{ fontSize: '48px', marginBottom: '12px' }}>❌</div>
                  <h5 className="fw-bold" style={{ color: 'var(--text-main)' }}>Application not approved</h5>
                  <p style={{ color: 'var(--text-muted2)', fontSize: '14px' }}>
                    {existing.rejectedReason || 'Your application was not approved at this time.'}
                  </p>
                </>
              )}
              {existing.status === 'paused' && (
                <>
                  <div style={{ fontSize: '48px', marginBottom: '12px' }}>⏸️</div>
                  <h5 className="fw-bold" style={{ color: 'var(--text-main)' }}>Account paused</h5>
                  <p style={{ color: 'var(--text-muted2)', fontSize: '14px' }}>Your affiliate account is currently paused. Contact support for help.</p>
                </>
              )}
            </div>
          </div>
        ) : (
          <div className="card border-0 shadow-sm" style={{ borderRadius: '16px' }}>
            <div className="card-body p-4">
              <h5 className="fw-bold mb-4" style={{ color: 'var(--text-main)' }}>Apply to become an affiliate</h5>

              {error && (
                <div className="alert alert-danger rounded-3 mb-3" style={{ fontSize: '14px' }}>{error}</div>
              )}

              <form onSubmit={handleSubmit}>

                {/* Country — first field, gates the rest */}
                <div className="mb-3">
                  <label className="form-label fw-semibold" style={{ color: 'var(--text-main)', fontSize: '14px' }}>
                    Your country <span style={{ color: '#dc2626' }}>*</span>
                  </label>
                  <select
                    className="form-select rounded-3"
                    value={form.country}
                    onChange={handleCountryChange}
                    required
                    style={{ fontSize: '14px' }}
                  >
                    <option value="">Select your country…</option>
                    {/* Supported countries */}
                    <optgroup label="✅ Supported countries">
                      {SUPPORTED_COUNTRIES.map(c => (
                        <option key={c.code} value={c.code}>{c.name}</option>
                      ))}
                    </optgroup>
                    {/* Common unsupported countries shown explicitly */}
                    <optgroup label="❌ Not yet supported">
                      <option value="IN">India</option>
                      <option value="BR">Brazil</option>
                      <option value="PK">Pakistan</option>
                      <option value="BD">Bangladesh</option>
                      <option value="NG">Nigeria</option>
                      <option value="PH">Philippines</option>
                      <option value="ID">Indonesia</option>
                      <option value="VN">Vietnam</option>
                      <option value="EG">Egypt</option>
                      <option value="KE">Kenya</option>
                      <option value="OTHER">Other country not listed</option>
                    </optgroup>
                  </select>
                  <div className="form-text">Payouts are sent via Stripe Connect. Only certain countries are supported.</div>
                </div>

                {/* Country not supported — show message, hide the rest of the form */}
                {countryNotSupported ? (
                  <div className="alert rounded-3 p-4" style={{ background: 'var(--orange-section)', border: '1px solid #fed7aa' }}>
                    <div style={{ fontSize: '24px', marginBottom: '8px' }}>🌍</div>
                    <h6 className="fw-bold" style={{ color: '#9a3412' }}>Payouts not yet available in your country</h6>
                    <p style={{ color: '#7c2d12', fontSize: '13px', lineHeight: 1.6, marginBottom: '12px' }}>
                      We use Stripe Connect to send affiliate payouts directly to bank accounts. Unfortunately Stripe Connect
                      is not yet available in your region, which means we cannot process payouts to you at this time.
                    </p>
                    <p style={{ color: '#7c2d12', fontSize: '13px', lineHeight: 1.6, margin: 0 }}>
                      You can still earn commission by sharing your referral link — we'll hold your balance and
                      pay you via an alternative method (PayPal, Wise, etc.) once we add support for your country.
                      <strong> Contact us </strong> at <a href={`mailto:${process.env.REACT_APP_SUPPORT_EMAIL || 'support@dreamybedtime.com'}`}
                        style={{ color: '#9a3412' }}>support@dreamybedtime.com</a> to be added to the waitlist.
                    </p>
                  </div>
                ) : form.country ? (
                  <>
                    <div className="mb-3">
                      <label className="form-label fw-semibold" style={{ color: 'var(--text-main)', fontSize: '14px' }}>
                        Your website or social media URL
                      </label>
                      <input
                        className="form-control rounded-3"
                        placeholder="https://instagram.com/yourhandle"
                        value={form.websiteUrl}
                        onChange={e => setForm(f => ({ ...f, websiteUrl: e.target.value }))}
                        style={{ fontSize: '14px' }}
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label fw-semibold" style={{ color: 'var(--text-main)', fontSize: '14px' }}>
                        Tell us about your audience
                      </label>
                      <textarea
                        className="form-control rounded-3"
                        rows={3}
                        placeholder="e.g. I'm a parenting blogger with 6,000 Instagram followers, mostly mums with toddlers."
                        value={form.bio}
                        onChange={e => setForm(f => ({ ...f, bio: e.target.value }))}
                        style={{ fontSize: '14px' }}
                      />
                    </div>

                    <div className="mb-4">
                      <label className="form-label fw-semibold" style={{ color: 'var(--text-main)', fontSize: '14px' }}>
                        How do you plan to promote Dreamy Bedtime?
                      </label>
                      <textarea
                        className="form-control rounded-3"
                        rows={2}
                        placeholder="e.g. Instagram posts, TikTok videos, blog post review"
                        value={form.promoMethod}
                        onChange={e => setForm(f => ({ ...f, promoMethod: e.target.value }))}
                        style={{ fontSize: '14px' }}
                      />
                    </div>

                    <button
                      type="submit"
                      className="btn w-100 rounded-pill fw-bold"
                      style={{ background: 'linear-gradient(135deg,#2d1b69,#6f42c1)', color: 'white', padding: '12px' }}
                      disabled={submitting}
                    >
                      {submitting ? <span className="spinner-border spinner-border-sm me-2"></span> : null}
                      Submit Application
                    </button>
                  </>
                ) : null}
              </form>
            </div>
          </div>
        )}

        <div className="text-center mt-4">
          <Link to="/" style={{ color: '#6f42c1', fontSize: '14px' }}>← Back to home</Link>
        </div>
      </div>
    </div>
  );
};

export default AffiliateApplyPage;
